import React, { useEffect, useRef, useState } from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { cn } from '@/lib/utils';
import type { GeocodedResult } from '@/hooks/useLocationSearch';
import { MANUAL_STOP_LIMITS, type StopKind } from '@/lib/validation/manualStopValidation';
import { AutocompleteDropdown } from './AutocompleteDropdown';
import { AddressVerification } from './AddressVerification';
import { FieldError } from './FieldError';
import type { ManualStopAddressState } from './useManualStopAddress';

const DEBOUNCE_MS = 400;

interface LocationFieldsProps {
  stopKind: StopKind;
  addressState: ManualStopAddressState;
  /** Errors keyed by form path (location.name, location.address, …). */
  errors: Record<string, string>;
}

/**
 * Location section of the manual stop form.
 * Business: name search (Google Places) + address search. Customer: street-address
 * search only — a person's name is never sent to a places search.
 */
// eslint-disable-next-line max-lines-per-function -- one cohesive form section
export const LocationFields: React.FC<LocationFieldsProps> = ({
  stopKind,
  addressState,
  errors
}) => {
  const { search } = addressState;
  const [nameDropdownOpen, setNameDropdownOpen] = useState(false);
  const [addressDropdownOpen, setAddressDropdownOpen] = useState(false);
  const nameTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const addressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const nameContainerRef = useRef<HTMLDivElement>(null);
  const addressContainerRef = useRef<HTMLDivElement>(null);
  const unitInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (nameContainerRef.current && !nameContainerRef.current.contains(e.target as Node)) {
        setNameDropdownOpen(false);
      }
      if (addressContainerRef.current && !addressContainerRef.current.contains(e.target as Node)) {
        setAddressDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Move focus to the unit field the moment USPS says one is required.
  useEffect(() => {
    if (addressState.result?.status === 'unit_missing' && !addressState.unit) {
      unitInputRef.current?.focus();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only react to a new result
  }, [addressState.result]);

  const handleNameChange = (value: string) => {
    search.updateLocationName(value);
    setNameDropdownOpen(true);
    if (nameTimerRef.current) clearTimeout(nameTimerRef.current);
    if (value.trim().length >= 3) {
      nameTimerRef.current = setTimeout(() => void search.searchByName(), DEBOUNCE_MS);
    }
  };

  const handleAddressChange = (value: string) => {
    search.updateAddress(value);
    setAddressDropdownOpen(true);
    if (addressTimerRef.current) clearTimeout(addressTimerRef.current);
    if (value.trim().length >= 5) {
      addressTimerRef.current = setTimeout(
        () => void search.searchByAddress({ addressOnly: stopKind === 'customer' }),
        DEBOUNCE_MS
      );
    }
  };

  const handleSelect = (result: GeocodedResult) => {
    void search.selectResult(result);
    setNameDropdownOpen(false);
    setAddressDropdownOpen(false);
  };

  const dropdownFor = (field: 'name' | 'address', open: boolean) => (
    <AutocompleteDropdown
      open={open && search.lastSearchedField === field}
      isSearching={search.isSearching && search.lastSearchedField === field}
      error={search.error && search.lastSearchedField === field ? search.error : null}
      results={search.results}
      onSelect={handleSelect}
    />
  );

  // City, state, ZIP and coordinates come from the picked suggestion — their errors show here.
  const addressError =
    errors['location.address'] ??
    errors['location.postal_code'] ??
    errors['location.city'] ??
    errors['location.region'] ??
    errors['location.lat'] ??
    errors['location.lng'];
  const unitError = errors['location.address_line2'];

  return (
    <div className='space-y-4'>
      {stopKind === 'business' && (
        <div className='space-y-2 relative' ref={nameContainerRef}>
          <Label htmlFor='location-name'>
            Location Name <span className='text-destructive'>*</span>
          </Label>
          <Input
            id='location-name'
            placeholder='Business name or landmark'
            value={search.locationName}
            onChange={e => handleNameChange(e.target.value)}
            onFocus={() => setNameDropdownOpen(true)}
            autoComplete='off'
            maxLength={MANUAL_STOP_LIMITS.locationName}
            className={cn(errors['location.name'] && 'border-destructive')}
          />
          {dropdownFor('name', nameDropdownOpen)}
          <FieldError message={errors['location.name']} />
        </div>
      )}

      <div className='space-y-2 relative' ref={addressContainerRef}>
        <Label htmlFor='address'>
          {stopKind === 'customer' ? 'Customer address' : 'Address'}{' '}
          <span className='text-destructive'>*</span>
        </Label>
        <Input
          id='address'
          placeholder='Start typing a street address'
          value={search.address}
          onChange={e => handleAddressChange(e.target.value)}
          onFocus={() => setAddressDropdownOpen(true)}
          autoComplete='off'
          maxLength={MANUAL_STOP_LIMITS.address}
          className={cn(addressError && 'border-destructive')}
        />
        {dropdownFor('address', addressDropdownOpen)}
        <FieldError message={addressError} />
      </div>

      <div className='space-y-2'>
        <Label htmlFor='address-line2'>
          {stopKind === 'customer' ? 'Apt / unit' : 'Suite / unit'} (if any)
        </Label>
        <Input
          id='address-line2'
          ref={unitInputRef}
          value={addressState.unit}
          onChange={e => addressState.setUnit(e.target.value)}
          autoComplete='off'
          maxLength={MANUAL_STOP_LIMITS.addressLine2}
          className={cn(unitError && 'border-destructive')}
        />
        <FieldError message={unitError} />
      </div>

      {addressState.fields && (
        <AddressVerification
          stopKind={stopKind}
          isChecking={addressState.isChecking}
          result={addressState.result}
          enteredAddress={search.address}
          suggestionChoice={addressState.suggestionChoice}
          onAcceptSuggestion={addressState.acceptSuggestion}
          onKeepEntered={addressState.keepEntered}
        />
      )}
    </div>
  );
};
